<template>
    <div class="live-grid">

        <!-- TOP: compact KPI strip — 1/2/3-column grid based on viewport. The
             .kpi-strip CSS makes every panel fill the grid cell vertically so
             they share a uniform height when side-by-side. -->
        <div class="kpi-strip">
            <ChartPanel
                :title="$t('realtime.visitors.count')"
                icon="bi-people"
                :loading="isLoadingTodayCount"
                :noZones="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                :noZonesText="$t('realtime.visitors.noEntrance')"
                :accent="'#36910d'"
            >
                <div class="kpi-center">
                    <div class="current-counter"><b>{{ totalTodaysCount }}</b></div>
                </div>
            </ChartPanel>

            <ChartPanel
                :title="$t('realtime.visitors.todayVsAverages')"
                icon="bi-bar-chart-line"
                :loading="isLoadingTodayCount"
                :empty="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                :accent="'#36910d'"
            >
                <div class="kpi-row">
                    <div class="kpi-pill" v-for="item in leftComparisons" :key="item.label">
                        <p class="kpi-value" :style="{ color: getDifferenceColor(item.diff) }">{{ formatDifference(item.diff) }}</p>
                        <span class="kpi-cap">{{ $t('realtime.vs') }}<br>{{ item.label }}</span>
                    </div>
                </div>
            </ChartPanel>

            <ChartPanel
                :title="$t('realtime.visitors.sameWeekday')"
                icon="bi-calendar-week"
                :loading="isLoadingTodayCount"
                :empty="!isEntranceZonePresent() && !isCombinedEntranceExitZonePresent()"
                :accent="'#36910d'"
            >
                <div class="kpi-row">
                    <div class="kpi-pill" v-for="item in weekdayComparisons" :key="item.label">
                        <p class="kpi-value" :style="{ color: getDifferenceColor(item.diff) }">{{ formatDifference(item.diff) }}</p>
                        <span class="kpi-cap">{{ $t('realtime.vs') }}<br>{{ item.label }}</span>
                    </div>
                </div>
            </ChartPanel>

            <ChartPanel
                v-if="!isCardEmpty(totalTodaysExit)"
                :title="$t('realtime.visitors.occupancy')"
                icon="bi-door-open"
                :loading="isLoadingTodayCount"
                :empty="isCardEmpty(totalTodaysExit)"
                :accent="'#B2C149'"
            >
                <div class="kpi-center">
                    <div class="current-counter"><b>{{ todaysCurrentCount - totalTodaysExit }}</b></div>
                </div>
            </ChartPanel>
        </div>

        <!-- BOTTOM: two live tables side-by-side — Zone Live (polygon) + Tripwire Activity -->
        <div class="live-tables">

            <ChartPanel
                class="auto-h panel-min-140"
                :title="$t('realtime.visitors.zoneTable')"
                icon="bi-table"
                :height="160"
                :loading="isLoadingTodayCount"
            >
                <div class="table-wrapper">
                <table class="stora-table">
                    <thead>
                    <tr>
                        <th>{{ $t('realtime.zone') }}</th>
                        <th>{{ $t('realtime.visitors.currentInZone') }}</th>
                        <!-- "Entries Today", not "Today's Count": this counts every
                             entry into the polygon, including people who walked
                             straight through. Read as a customer count it is wildly
                             high — checkout zones ran 4x the store's footfall. The
                             engaged-only view lives in Today's Real-Time Opportunity
                             Data, which is where that distinction belongs. -->
                        <th>{{ $t('realtime.visitors.entriesToday') }}</th>
                        <th>{{ $t('realtime.visitors.averageDuration') }}</th>
                        <th>{{ $t('realtime.status') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        v-for="([zoneName, zoneData]) in polygonZoneRows"
                        :key="zoneName"
                    >
                        <td :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style" :title="zoneTypeTitle(zoneData.zoneType)">
                            <b>{{ zoneName }}</b>
                            <Tooltip
                                v-if="zoneData.hasTripwire"
                                :text="$t('realtime.visitors.alsoTripwire')"
                                placement="right"
                            >
                                <i class="bi bi-arrow-left-right geom-marker"></i>
                            </Tooltip>
                        </td>
                        <td class="num" :style="getZoneStyleCurrentIn(zoneData.flowId, zoneData.zoneType)">
                            <b>{{ flowZoneCounters[zoneData.flowId]?.[zoneName] || 0 }}</b>
                        </td>
                        <td class="num" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
                            {{ zoneData.todaysCount }}
                        </td>
                        <td class="num" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
                            {{ formattedDurationTime(zoneData.averageDuration ?? 0) }}
                        </td>
                        <td class="icon-cell">
                            <span v-if="getZoneStyle(zoneData.flowId, zoneData.zoneType).isStreamActive">
                                <i class="bi" :class="[streamChip(zoneData.flowId).icon, streamChip(zoneData.flowId).tone === 'ok' ? 'text-ok' : 'text-warn']" :title="streamChip(zoneData.flowId).tooltip || streamChip(zoneData.flowId).label"></i>
                            </span>
                        </td>
                    </tr>
                    </tbody>
                </table>
                </div>
            </ChartPanel>

            <ChartPanel
                class="auto-h panel-min-140"
                :title="$t('realtime.visitors.tripwireTable')"
                icon="bi-arrow-left-right"
                :height="160"
                :loading="isLoadingTodayCount"
                :empty="!tripwireZoneRows.length"
                :emptyText="$t('realtime.visitors.noTripwires')"
            >
                <div class="table-wrapper">
                <table class="stora-table">
                    <thead>
                    <tr>
                        <th>{{ $t('realtime.zone') }}</th>
                        <th>{{ $t('realtime.visitors.inbound') }}</th>
                        <th>{{ $t('realtime.visitors.outbound') }}</th>
                        <th>{{ $t('realtime.visitors.shareOfTotal') }}</th>
                        <th>{{ $t('realtime.status') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        v-for="([zoneName, zoneData]) in tripwireZoneRows"
                        :key="zoneName"
                        :class="{ 'tripwire-flash': tripwireFlashState[`${zoneData.flowId}::${zoneName}`] }"
                    >
                        <td :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style" :title="zoneTypeTitle(zoneData.zoneType)">
                            <b>{{ zoneName }}</b>
                        </td>
                        <td class="num" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
                            <b>{{ zoneData.directionalInCount ?? 0 }}</b>
                        </td>
                        <td class="num" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
                            {{ zoneData.directionalOutCount ?? 0 }}
                        </td>
                        <td class="num" :style="getZoneStyle(zoneData.flowId, zoneData.zoneType).style">
                            <div class="visitor-bar-outer">
                                <div
                                    :class="['visitor-bar-gradient', {
                                        'inactive': !getZoneStyle(zoneData.flowId, zoneData.zoneType).isStreamActive,
                                        'zero-fill': (zoneData.directionalInCount ?? 0) === 0
                                    }]"
                                    :style="{ '--bar-fill': totalTodaysCount > 0 ? (((zoneData.directionalInCount ?? 0) / totalTodaysCount) * 100) + '%' : '0%' }"
                                />
                                <span v-if="getZoneStyle(zoneData.flowId, zoneData.zoneType).isStreamActive" class="visitor-bar-label">
                                    {{ totalTodaysCount > 0 ? (((zoneData.directionalInCount ?? 0) / totalTodaysCount) * 100).toFixed(1) + '%' : '0%' }}
                                </span>
                            </div>
                        </td>
                        <td class="icon-cell">
                            <span v-if="getZoneStyle(zoneData.flowId, zoneData.zoneType).isStreamActive">
                                <i class="bi" :class="[streamChip(zoneData.flowId).icon, streamChip(zoneData.flowId).tone === 'ok' ? 'text-ok' : 'text-warn']" :title="streamChip(zoneData.flowId).tooltip || streamChip(zoneData.flowId).label"></i>
                            </span>
                        </td>
                    </tr>
                    </tbody>
                </table>
                </div>
            </ChartPanel>

        </div>
    </div>
</template>

<script>
import ChartPanel from '@/components/ui/ChartPanel.vue'
import Tooltip from '@/components/ui/Tooltip.vue'

import { onBeforeMount, onMounted, onBeforeUnmount, ref, computed, watch } from 'vue'

import io from 'socket.io-client'
import { deriveStreamChip } from '@/composables/streamStatus'
import { useI18n } from 'vue-i18n'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

import { useCameraStreamStore } from '@/stores/CameraStreamStore'
import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import { parseDeviceUrl, liveDeviceUrl, isLiveRelayUrl, liveProbeUrl } from '@/api/deviceUrl'

import { detection_zone_colors_by_name_solid } from '@/constants.js'
import { getCurrentCountersShared, abortGetCurrentCounters, getPastAverages } from "@/api/eventsAPI.js"

export default {
    name: 'CurrentLiveVisitorsData',

    components: {
        ChartPanel,
        Tooltip
    },

    setup(props, { emit }) {
        const { t } = useI18n()
        const CameraStreamStore = useCameraStreamStore()
        const userStore = useUserStore()
        const companyStore = useCompanyStore()

        const cameraHeartBeat = ref({})
        const heartbeatTimeouts = ref({})

        // Latest rich `stream_status` per flowId (new supervisor-equipped devices only).
        const streamRichStatus = ref({})

        const totalTodaysCount = ref(0)
        const totalTodaysExit = ref(0)
        const pastAverages = ref({})
        const currentWeekday = ref(null)
        const isLoadingTodayCount = ref(true)
        const isLoadingTodayCountTable = ref(true)
        const processedZones = ref({})
        const flowZoneCounters = ref({})
        // Live today-totals for tripwires, keyed flowId → zoneName → { in, out }.
        // Mirrors flowZoneCounters but tracks tripwire crossing tallies (cumulative for the day)
        // rather than current polygon population.
        const flowTripwireCounters = ref({})
        // Most recent date stamp received in a zone_counter payload — used to detect
        // local-time day rollovers and refresh the API on the next tick if needed.
        const tripwireCounterDate = ref(null)
        // Map of (flowId, zoneName) → timestamp of last crossing animation, used to
        // briefly highlight the matching row when a tripwire_crossing event arrives.
        const tripwireFlashState = ref({})
        const streamToFlowMap = ref({})
        const streamStatuses = ref({})

        const fetchInterval = ref(null)
        const isMounted = ref(true)
        let currentFetchController = null
        
        const socket = ref(null)
        const isConnectingSocket = ref(false)
        const socketConnected = ref(true)

        const socketioURL = computed(() => {
            return {
                // Live events from the box (tunnel) or, on LiveTransport 'iothub', from
                // the portal's live relay; same events either way. See @/api/deviceUrl.
                URLDevice: liveDeviceUrl(companyStore.company)
            }
        })

        const formattedDurationTime = computed(() => {
            return (seconds) => {
                if (typeof seconds !== 'number' || isNaN(seconds)) {
                    return t('realtime.notAvailable'); // Handle invalid input
                }

                if (seconds < 60) {
                    return `${seconds.toFixed(0)} sec`
                } else if (seconds < 3600) {
                    const minutes = Math.floor(seconds / 60)
                    const remainingSeconds = seconds % 60
                    return `${minutes} min ${remainingSeconds.toFixed(0)} sec`
                } else {
                    const hours = Math.floor(seconds / 3600)
                    const remainingMinutes = Math.floor((seconds % 3600) / 60)
                    const remainingSeconds = seconds % 60
                    return `${hours} ${t('realtime.hourShort')} ${remainingMinutes} min ${remainingSeconds.toFixed(0)} sec`
                }
            }
        })

        const currentWeekdayString = computed(() => {
            if (currentWeekday.value >= 0 && currentWeekday.value <= 6) {
                const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                return daysOfWeek[currentWeekday.value]
            } else {
                return 'Same Weekday'
            }
        })

        // The weekday in the comparison labels ("Monday's" / "maandagen"); the
        // English name above stays as it was.
        const weekdayPlural = computed(() =>
            currentWeekday.value >= 0 && currentWeekday.value <= 6
                ? t(`realtime.weekdayPlural.${currentWeekday.value}`)
                : t('realtime.weekdayPlural.same'))

        const leftComparisons = computed(() => [
            { label: t('realtime.visitors.vsYesterday'),  diff: totalTodaysCount.value - pastAverages.value.past_averages.yesterday },
            { label: t('realtime.visitors.vsPast7'), diff: totalTodaysCount.value - pastAverages.value.past_averages.past_7_days },
            { label: t('realtime.visitors.vsPast30'), diff: totalTodaysCount.value - pastAverages.value.past_averages.past_30_days },
            { label: t('realtime.visitors.vsLastWeek'), diff: totalTodaysCount.value - pastAverages.value.past_averages.last_week },
            { label: t('realtime.visitors.vsLastMonth'), diff: totalTodaysCount.value - pastAverages.value.past_averages.last_month },
        ])

        const weekdayComparisons = computed(() => [
            { label: t('realtime.visitors.weekdayPast30', { days: weekdayPlural.value }),  diff: totalTodaysCount.value - pastAverages.value.past_averages_same_weekday.past_30_days },
            { label: t('realtime.visitors.weekdayPast365', { days: weekdayPlural.value }), diff: totalTodaysCount.value - pastAverages.value.past_averages_same_weekday.past_365_days },
            { label: t('realtime.visitors.weekdayLastMonth', { days: weekdayPlural.value }),    diff: totalTodaysCount.value - pastAverages.value.past_averages_same_weekday.last_month },
        ])

        const stopInterval = () => {
            if (fetchInterval.value) {
                clearInterval(fetchInterval.value)
                fetchInterval.value = null
            }
        }

        const startInterval = () => {
            stopInterval() // Ensure any existing interval is cleared
            fetchInterval.value = setInterval(async () => {
                if (!isMounted.value) return // Exit if the component is not mounted
                await refreshData()
            }, 5000) // Refresh every 5 seconds
        }

        const refreshData = async () => {
            if (currentFetchController) {
                currentFetchController.abort() // Abort any ongoing fetch request
            }

            // if parent told us to stop, do nothing
            if (props.stopPollingCurrentCounters) {
                return
            }

            const controller = new AbortController()
            currentFetchController = controller

            try {
                const current_counters_data = await getCurrentCountersShared()
                if (isMounted.value) {
                    processedZones.value = processZonesData(current_counters_data)
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch aborted')
                } else {
                    console.error('Error fetching counters:', error)
                }
            }
        }

        const fetchStreamStatuses = async () => {
            const flowIds = Object.values(processedZones.value)
                .map(zone => zone.flowId) // Extract the flowId for each zone
                .filter((flowId, index, self) => self.indexOf(flowId) === index)
            
            for (const flowId of flowIds) {
                await CameraStreamStore.fetchInputCameraStreambyFlowId(flowId)
                const cameraStream = CameraStreamStore.inputcamerastream

                // Store the StreamStatus
                if (cameraStream && cameraStream.FlowId) {
                    streamStatuses.value[flowId] = cameraStream.StreamStatus
                }
            }
        }

        // Stream-health chip: rich supervisor status when present, heartbeat watchdog otherwise.
        const streamChip = (flowId) => {
            const rich = streamRichStatus.value[flowId]
            return deriveStreamChip({
                dbStreamStatus: streamStatuses.value[flowId] ?? false,
                richStatus: rich || null,
                richStatusAt: rich?._at ?? null,
                heartbeatAlive: !!cameraHeartBeat.value[flowId],
            })
        }

        const getZoneStyle = (flowId, zoneType) => {
            const isStreamActive = streamStatuses.value[flowId] ?? false; // Default to false
            return {
                isStreamActive, // Include the stream activity status
                style: {
                    // No textAlign here on purpose. It used to say 'left', and an
                    // inline style beats any class selector — so the .num rule that
                    // centres the numeric columns was silently overridden and the
                    // values sat left under centred headers. Alignment is CSS's job:
                    // td defaults to left, td.num centres.
                    color: isStreamActive ? '#425461' : '#D3D3D3',
                    padding: '8px',
                    fontSize: 'small',
                },
            }
        }

        const getZoneStyleCurrentIn = (flowId, zoneType) => {
            const isStreamActive = streamStatuses.value[flowId] ?? false; // Default to false
            return {
                // See getZoneStyle: alignment is left to CSS so .num can win.
                color: isStreamActive ? getColorForZone(zoneType) : '#D3D3D3',
                padding: '8px',
                fontSize: 'small',
            }
        }

        const formatDifference = (difference) => {
            const roundedValue = Math.round(difference)
            return roundedValue > 0 ? `+${roundedValue}` : `${roundedValue}`
        }

        const getDifferenceColor = (difference) => {
            return difference > 0 ? '#36910d' : '#C21500'
        }

        const isCardEmpty = (...values) => {
            // Check if all values in the card are null, 0, empty string, or empty object
            return values.every(
                (val) =>
                    val === null ||
                    val === "null" ||
                    val === 0 ||
                    val === "0" ||
                    val === "" ||
                    (typeof val === "object" && Object.keys(val).length === 0)
            )
        }

        const detectNetworkContext = async (publicURL) => {
            try {
                const controller = new AbortController()
                const signal = controller.signal
                const timeout = 5000 // 5 seconds timeout for checking public IP

                // The box answers on its root (tunnel); the live relay on /status.
                const probeUrl = isLiveRelayUrl(publicURL) ? liveProbeUrl(publicURL) : parseDeviceUrl(publicURL).httpBase
                const fetchPromise = fetch(probeUrl, { signal })
                setTimeout(() => controller.abort(), timeout)

                const response = await fetchPromise
                if (response.ok) {
                    return publicURL
                }
            } catch (error) {
                console.error('Public Socket URL is not accessible')
                toast.error(t('realtime.deviceNotAccessible'))
                throw new Error('Public Socket URL is not accessible')
            }
            throw new Error('Public Socket URL is not accessible')
        }

        const establishSocketConnection = async (publicURL) => {
            if (socket.value !== null) {
                console.log("Returning existing socket")
                return socket.value
            }

            if (isConnectingSocket.value) {
                console.log('Already connecting, waiting...')
                while (isConnectingSocket.value) {
                    await new Promise(r => setTimeout(r, 500))
                }
                return socket.value
            }

            console.log("Connecting socket ...")
            isConnectingSocket.value = true

            try {
                const url = await detectNetworkContext(publicURL)
                // console.log('Network context detected, URL:', url)

                // io(url) would read "/device/<id>" as a NAMESPACE, not a path prefix — it
                // would ask for a namespace the edge does not define, over a socket.io path
                // nginx does not route. Origin and path must be passed separately.
                // For the legacy ":500x" shape these are the whole URL and "/socket.io",
                // i.e. exactly what io(url) resolved to on its own. See @/api/deviceUrl.
                const { origin, socketPath, proxied } = parseDeviceUrl(url)

                socket.value = io(origin, {
                    path: socketPath,
                    // Same-origin once this store is cut over, so this is a no-op that
                    // records the intent. NOT unconditional: on the legacy cross-origin
                    // shape the edge replies "Access-Control-Allow-Origin: *", which a
                    // browser REFUSES for a credentialed request — it would break the
                    // live view of every store not yet cut over.
                    withCredentials: proxied,
                    transports: ['websocket'],
                    reconnection: true,
                    // Infinity, not 5: after 5 attempts (~5s) socket.io gives up permanently and only a
                    // page reload recovers. A tunnel drop legitimately lasts up to ~60s while sshd frees
                    // the device's 500x port. See NavSidebar.vue for the full note.
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 10000,
                    debug: true,
                    perMessageDeflate: false,
                    timeout: 120000,
                    maxPayload: 50 * 1024 * 1024,
                })

                setupSocketEvents(socket.value)

                return socket.value

            } catch (error) {
                console.error('Error establishing socket connection:', error)
                throw error
            } finally {
                console.log('Resetting serverConnection flag')
                isConnectingSocket.value = false
            }
        }

        const setupSocketEvents = (socketInstance) => {
            socketInstance.on('zone_counter', (data) => {
                const streamId = data.stream_id
                const flowId = streamToFlowMap.value[streamId]

                if (!flowId) return

                // Polygon current-population counts (existing behaviour)
                if (data.zone_counter) {
                    flowZoneCounters.value = {
                        ...flowZoneCounters.value,
                        [flowId]: {
                            ...flowZoneCounters.value[flowId],
                            ...data.zone_counter,
                        },
                    }
                }

                // Tripwire today-totals piggybacked on the same payload.
                // Shape: { [zoneName]: { in: number, out: number } }
                if (data.tripwire_counter) {
                    flowTripwireCounters.value = {
                        ...flowTripwireCounters.value,
                        [flowId]: {
                            ...flowTripwireCounters.value[flowId],
                            ...data.tripwire_counter,
                        },
                    }
                }

                // Detect local day rollovers (edge resets its today-counters at midnight).
                // When the date changes, force an API refresh so we resync.
                if (data.date && data.date !== tripwireCounterDate.value) {
                    tripwireCounterDate.value = data.date
                    // Re-fetch authoritative totals; non-blocking.
                    refreshData()
                }
            })

            // Per-crossing instant event from the edge — used to briefly flash the
            // affected row in the live table for visual feedback.
            socketInstance.on('tripwire_crossing', (data) => {
                const streamId = data.stream_id
                const flowId = streamToFlowMap.value[streamId]
                if (!flowId || !data.zone_name) return
                const key = `${flowId}::${data.zone_name}`
                tripwireFlashState.value = { ...tripwireFlashState.value, [key]: Date.now() }
                setTimeout(() => {
                    if (tripwireFlashState.value[key]) {
                        const next = { ...tripwireFlashState.value }
                        delete next[key]
                        tripwireFlashState.value = next
                    }
                }, 800)
            })

            socketInstance.on('heartbeat', (data) => {
                const { streamId, heartbeat } = data

                // Ensure the flowId is mapped correctly to the streamId
                const flowId = streamToFlowMap.value[streamId]

                if (flowId) {
                    // Initialize or update the heartbeat value for the specific flowId
                    cameraHeartBeat.value[flowId] = heartbeat

                    // Clear any existing timeout for this flowId
                    if (heartbeatTimeouts.value[flowId]) {
                        clearTimeout(heartbeatTimeouts.value[flowId])
                    }

                    if (heartbeat) {
                        // Reset the heartbeat timeout for 5 seconds
                        heartbeatTimeouts.value[flowId] = setTimeout(() => {
                            cameraHeartBeat.value[flowId] = false
                        }, 5000)
                    }
                }
            })

            // Rich health from the new edge stream-supervisor (new devices only).
            socketInstance.on('stream_status', (data) => {
                const flowId = streamToFlowMap.value[data.streamId]
                if (flowId) {
                    streamRichStatus.value[flowId] = { ...data, _at: Date.now() }
                }
            })

            socketInstance.on('disconnection_message', (data) => {
                    console.log('message on disconnection: ', data)
                })

            socketInstance.on('error', (error) => {
            console.error('Error received from server:', error)     
            toast.error(t('realtime.error', { message: error.message }))
            })
        }

        
        const initializeSocketConnection = async () => {
            if (socketioURL.value.URLDevice) {
            try {
                socket.value = await establishSocketConnection(socketioURL.value.URLDevice)
                console.log('Socket connection Home established')
            } catch (error) {
                console.error('Failed to establish Home socket connection:', error)
                socketConnected.value = false
            }
            } else {
            console.error('socketioURL is not defined')
            socketConnected.value = false
            }
        }

        const initializeFlowData = () => {
            streamToFlowMap.value = CameraStreamStore.inputcamerastreams.reduce((acc, item) => {
                acc[item.CameraStreamId] = item.FlowId
                return acc
            }, {})
        }

        onBeforeMount(async () => {
            await companyStore.fetchCompanyDetails(userStore.currentCompany.CompanyId)
            await CameraStreamStore.fetchInputCameraStreams()
            await initializeSocketConnection()
            initializeFlowData()
        })

        onMounted(async () => {
            isMounted.value = true

            const current_counters_data = await getCurrentCountersShared()
            processedZones.value = processZonesData(current_counters_data)

            pastAverages.value = await getPastAverages()
            currentWeekday.value = pastAverages.value['current_weekday']

            isLoadingTodayCount.value = false

            // Initialize flowZoneCounters with default zero values
            for (const flowId in current_counters_data.current_counters_data) {
                const flowData = current_counters_data.current_counters_data[flowId]
                const counters = flowData.counters || {} // Get counters for this flowId

                // Ensure structure for the flowId
                if (!flowZoneCounters.value[flowId]) {
                    flowZoneCounters.value[flowId] = {}
                }

                // Initialize detection zones for the flowId
                for (const zoneName in counters) {
                    if (!flowZoneCounters.value[flowId][zoneName]) {
                        flowZoneCounters.value[flowId][zoneName] = 0 // Default value
                    }
                }
            }

            await fetchStreamStatuses(current_counters_data.current_counters_data)
            
            startInterval()
            
            isLoadingTodayCountTable.value = false
        })

        const processZonesData = (data) => {
            const zones = {}

            const flowData = data.current_counters_data

            if (!flowData) {
                console.warn("No 'current_counters_data' found")
                return zones
            }

            for (const flowId in flowData) {
                const flow = flowData[flowId]

                // A zone may appear in flow.counters (polygon zones), flow.store_counters
                // (Combined EE polygons + any zone with a tripwire), or both.
                // Iterate the union so tripwire-only zones aren't dropped.
                const zoneNames = new Set([
                    ...Object.keys(flow.counters || {}),
                    ...Object.keys(flow.store_counters || {}),
                ])

                for (const zoneName of zoneNames) {
                    const counter = flow.counters?.[zoneName] || null
                    const storeCounter = flow.store_counters?.[zoneName] || null

                    // Live tripwire today-totals (only meaningful when the zone has a real
                    // tripwire — Combined EE polygons emit store_in/out via the polygon
                    // direction logic and aren't tracked by the edge's tripwire counter).
                    const liveTripwireIn = flowTripwireCounters.value[flowId]?.[zoneName]?.in
                    const apiTripwireIn = storeCounter?.zone_in_count ?? null
                    // Live counter overrides the API value only when higher — edge-restart safe.
                    const directionalInCount = (liveTripwireIn != null && apiTripwireIn != null)
                        ? Math.max(liveTripwireIn, apiTripwireIn)
                        : (liveTripwireIn ?? apiTripwireIn)

                    const liveTripwireOut = flowTripwireCounters.value[flowId]?.[zoneName]?.out
                    const apiTripwireOut = storeCounter?.zone_out_count ?? null
                    const directionalOutCount = (liveTripwireOut != null && apiTripwireOut != null)
                        ? Math.max(liveTripwireOut, apiTripwireOut)
                        : (liveTripwireOut ?? apiTripwireOut)

                    const polygonInCount = counter?.zone_in_count ?? null

                    // Today's count priority: directional (tripwire / Combined EE store_in)
                    // wins when present, otherwise fall back to the polygon visit count.
                    const todaysCount = directionalInCount ?? polygonInCount ?? 0

                    // hasPolygon: backend now omits tripwire-only zones from `counters`,
                    //   so this is true iff the zone actually has a polygon geometry.
                    // hasTripwire: backend marks zones with a real tripwire geometry via
                    //   the is_tripwire flag — Combined EE polygons stay false here.
                    // hasDirectional: any zone whose backend response carries store_counters,
                    //   covering both Combined EE polygons and tripwire-bearing zones.
                    zones[zoneName] = {
                        flowId,
                        todaysCount,
                        directionalInCount,
                        directionalOutCount,
                        averageDuration: counter?.average_duration ?? null,
                        zoneType: counter?.zone_type || storeCounter?.zone_type || 'Unknown Zone',
                        hasPolygon: counter != null,
                        hasTripwire: storeCounter?.is_tripwire === true,
                        hasDirectional: storeCounter != null,
                    }
                }
            }

            totalTodaysCount.value = Object.values(zones)
                .filter(zone => zone.zoneType === "Entrance Zone" || zone.zoneType === "Combined Entrance Exit Zone")
                .reduce((sum, zone) => sum + (zone.todaysCount || 0), 0)

            return zones
        }

        const getColorForZone = (zoneType) => {
            return detection_zone_colors_by_name_solid[zoneType] || 'black'
        }


        
        const isEntranceZonePresent = () => {
            return Object.values(processedZones.value).some(zone => zone.zoneType === "Entrance Zone")
        }

        const isCombinedEntranceExitZonePresent = () => {
            return Object.values(processedZones.value).some(zone => zone.zoneType === "Combined Entrance Exit Zone")
        }

        const isExitZonePresent = () => {
            return false
        }

        // -------------------------------------------------------------------
        // Row sources for the two live tables
        // -------------------------------------------------------------------

        // Zone Live Table — polygon-bearing zones, excluding Cash Register Zone.
        // Tripwire-only zones are intentionally not here; they appear in the
        // Tripwire Activity table below.
        const polygonZoneRows = computed(() =>
            Object.entries(processedZones.value)
                .filter(([, z]) => z.hasPolygon && z.zoneType !== 'Cash Register Zone')
        )

        // Tripwire Activity table — zones whose backend store_counter is flagged
        // is_tripwire (Combined EE polygons are NOT here even though they have
        // store_counter, because their direction comes from polygon-overlap, not
        // an actual tripwire geometry). Sort by inbound count descending.
        const tripwireZoneRows = computed(() =>
            Object.entries(processedZones.value)
                .filter(([, z]) => z.hasTripwire)
                .sort(([, a], [, b]) => (b.directionalInCount ?? 0) - (a.directionalInCount ?? 0))
        )

        onBeforeUnmount(() => {
            // Clear the interval when the component unmounts
            isMounted.value = false
            abortGetCurrentCounters()
            stopInterval()
            if (currentFetchController) {
                currentFetchController.abort()
            }
            if (socket.value) {
                socket.value.off('zone_counter')
                socket.value.off('tripwire_crossing')
                socket.value.off('stream_status')
                socket.value.off('error')
                socket.value.off('connect')
                socket.value.disconnect()
            }
        })

        // Zone type in the cell tooltip: shown in the reader's language; the
        // English name stays the data everything else compares on.
        const zoneTypeTitle = (zoneType) =>
            zoneType === 'Unknown Zone' ? t('realtime.unknownZone') : zoneTypeLabel(zoneType)

        return {
            zoneTypeTitle,
            isLoadingTodayCount,
            isLoadingTodayCountTable,
            cameraHeartBeat,
            streamChip,
            formattedDurationTime,
            getColorForZone,
            formatDifference,
            getDifferenceColor,
            isCardEmpty,
            totalTodaysCount,
            totalTodaysExit,
            pastAverages,
            currentWeekdayString,
            processedZones,
            polygonZoneRows,
            tripwireZoneRows,
            flowZoneCounters,
            flowTripwireCounters,
            tripwireFlashState,
            getZoneStyle,
            getZoneStyleCurrentIn,
            isEntranceZonePresent,
            isCombinedEntranceExitZonePresent,
            isExitZonePresent,
            leftComparisons,
            weekdayComparisons,
        }
    }
}

</script>

<style scoped>
.panel-min-140 :deep(.panel-body) { min-height: 140px; }

/* New layout: KPI strip on top, then live tables row below.
   Collapses to a single column on narrow screens. */
.live-grid{
  display: grid;
  grid-template-rows: auto auto;
  gap: 16px;
  align-items: start;
  margin-top: 12px;
}

/* KPI strip — explicit breakpoints (auto-fit produced unpredictable heights at
   intermediate widths). All panels in a row are forced to equal height via
   align-items: stretch (default) plus the panel filling its grid cell below. */
.kpi-strip{
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (min-width: 720px) and (max-width: 1099px) {
  .kpi-strip{ grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 1100px) {
  .kpi-strip{ grid-template-columns: repeat(3, 1fr); }
}
/* When Current Occupancy is also shown (4th panel), let it wrap to a 2nd row
   gracefully on the 3-col layout. The 4th item just spans whatever's left. */

/* Make every panel inside the strip fill the grid cell vertically and share
   the tallest's height. Combined with align-items: stretch (default) on the
   grid, this gives uniform heights across the row. */
.kpi-strip > :deep(.panel){
  height: 100%;
  display: flex;
  flex-direction: column;
}
.kpi-strip > :deep(.panel) .panel-body{
  flex: 1;
  min-height: 200px;       /* prevents the Visitor Count card from collapsing */
  height: auto;            /* override the prop-driven --panel-h */
}

/* Live tables row — Zone Live (polygon) + Tripwire Activity, side by side on wide screens */
.live-tables{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}

/* Let the ChartPanel grow with its children */
.auto-h:deep(.panel-body){
  height: auto !important;
}
.auto-h:deep(.panel-slot){
  height: auto !important;
}

.kpi-row {
  display: grid;
  /* Compact min so a 5-pill panel can fit 2-3 pills per row in even a narrow column.
     Combined with the panel min-height, the panel doesn't grow vertically beyond
     a few rows of pills. */
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
  align-items: stretch;
}

.kpi-pill {
  background: #f7f9fb;
  border: 1px solid #e6edf3;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
}

.kpi-pill p {
  margin: 0 0 2px 0;
  font-size: 1rem;
  font-weight: 400;
}

.kpi-cap {
  color: #6b7a86;
  font-size: 0.72rem;
  line-height: 1.15;
}

/* Keep the big number center-aligned like other KPIs */
.kpi-center{ 
  height:100%; display:flex; align-items:center; justify-content:center; 
}
.current-counter{ 
  font-size: clamp(42px, 3.2vw, 56px); color:#36910d; 
}

.text-ok{ color:#2F9E44; font-size:1.2rem; }
.text-warn{ color:#ffd32c; font-size:1.2rem; }

.table-wrapper{ border:1px solid #e6edf3; border-radius:10px; overflow:hidden; }
.stora-table{ width:100%; border-collapse:collapse; }
.stora-table th, .stora-table td{ padding:10px; border-bottom:1px solid #eef3f7; }
.stora-table thead th{
  background:rgba(33,37,41,.03); color:#4F7EB3; text-align:center; font-weight:700; font-size:.9rem;
}
.stora-table tbody tr:last-child td{ border-bottom:0; }
/* Numeric columns centre under their (already centred) headers. The zone-name
   column stays left-aligned — it's the only text column and it anchors the row. */
.stora-table td.num{ text-align:center; }
.icon-cell{ text-align:center; }

/* Cell that doesn't apply to this zone's geometry (e.g. dwell duration on a tripwire-only zone) */
.na-cell{ color:#aab4be; font-weight:600; }

/* Small inline glyph next to a zone name to indicate a tripwire is involved */
.geom-marker{ margin-left:6px; color:#4F7EB3; font-size:.78rem; opacity:.85; }

/* "in" / "out" suffixes alongside directional counts */
.dir-label{ color:#7a8a96; font-size:.78rem; font-weight:500; margin-left:2px; }

/* Brief flash on a row when a tripwire crossing arrives via socket */
.tripwire-flash td{ animation: tripwire-flash-anim .8s ease-out; }
@keyframes tripwire-flash-anim{
  0%   { background:rgba(178, 193, 73, .35); }
  100% { background:transparent; }
}

@media (max-width: 1200px){
  .live-tables{ grid-template-columns: 1fr; }
}
</style>